
import React, { useState, useRef, useEffect, useCallback } from 'react';
import './VisualToolsPage.css';
import PageHeader from '../../components/shared/PageHeader';
import Button from '../../components/shared/Button';
import Badge from '../../components/shared/Badge';
import { useToast } from '../../context/ToastContext';

type Tool = 'pen' | 'rect' | 'circle' | 'line' | 'text' | 'eraser' | 'select';
type ToolbarPanel = 'tools' | 'colors' | 'export' | null;

const TOOLS: { id: Tool; icon: string; label: string }[] = [
  { id: 'select', icon: '↖', label: 'Select' },
  { id: 'pen', icon: '✏', label: 'Pen' },
  { id: 'line', icon: '╱', label: 'Line' },
  { id: 'rect', icon: '▭', label: 'Rectangle' },
  { id: 'circle', icon: '○', label: 'Circle' },
  { id: 'text', icon: 'T', label: 'Text' },
  { id: 'eraser', icon: '◻', label: 'Eraser' },
];

const COLORS = [
  '#E5E7EB', '#1282D6', '#22C55E', '#EF4444', '#F59E0B',
  '#8B5CF6', '#EC4899', '#06B6D4', '#F97316', '#FFFFFF',
  '#000000', '#64748B',
];

const TEMPLATES = [
  { id: 'blank', label: 'Blank Canvas', icon: '□' },
  { id: 'wireframe', label: 'App Wireframe', icon: '⊞' },
  { id: 'flowchart', label: 'Flowchart', icon: '⇢' },
  { id: 'mindmap', label: 'Mind Map', icon: '✦' },
];

interface DrawElement {
  id: string;
  tool: Tool;
  color: string;
  lineWidth: number;
  points?: { x: number; y: number }[];
  x?: number; y?: number; w?: number; h?: number;
  text?: string;
}

export default function VisualToolsPage() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [tool, setTool] = useState<Tool>('pen');
  const [color, setColor] = useState('#E5E7EB');
  const [lineWidth, setLineWidth] = useState(2);
  const [isDrawing, setIsDrawing] = useState(false);
  const [elements, setElements] = useState<DrawElement[]>([]);
  const [currentEl, setCurrentEl] = useState<DrawElement | null>(null);
  const [undoStack, setUndoStack] = useState<DrawElement[][]>([]);
  const [panel, setPanel] = useState<ToolbarPanel>(null);
  const [zoom, setZoom] = useState(100);
  const startPos = useRef<{ x: number; y: number } | null>(null);
  const lastPos = useRef<{ x: number; y: number } | null>(null);
  const { addToast } = useToast();

  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const src = 'touches' in e ? e.touches[0] : e;
    return {
      x: (src.clientX - rect.left) * (canvas.width / rect.width),
      y: (src.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Grid
    ctx.strokeStyle = 'rgba(255,255,255,0.04)';
    ctx.lineWidth = 1;
    for (let x = 0; x < canvas.width; x += 24) {
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, canvas.height); ctx.stroke();
    }
    for (let y = 0; y < canvas.height; y += 24) {
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(canvas.width, y); ctx.stroke();
    }

    const drawEl = (el: DrawElement) => {
      ctx.strokeStyle = el.color;
      ctx.fillStyle = el.color;
      ctx.lineWidth = el.lineWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      if (el.tool === 'pen' && el.points && el.points.length > 1) {
        ctx.beginPath();
        ctx.moveTo(el.points[0].x, el.points[0].y);
        el.points.slice(1).forEach(p => ctx.lineTo(p.x, p.y));
        ctx.stroke();
      } else if (el.tool === 'eraser' && el.points && el.points.length > 1) {
        ctx.globalCompositeOperation = 'destination-out';
        ctx.beginPath();
        ctx.moveTo(el.points[0].x, el.points[0].y);
        el.points.slice(1).forEach(p => ctx.lineTo(p.x, p.y));
        ctx.lineWidth = el.lineWidth * 4;
        ctx.stroke();
        ctx.globalCompositeOperation = 'source-over';
      } else if (el.tool === 'rect' && el.x != null && el.y != null && el.w != null && el.h != null) {
        ctx.strokeRect(el.x, el.y, el.w, el.h);
      } else if (el.tool === 'circle' && el.x != null && el.y != null && el.w != null && el.h != null) {
        ctx.beginPath();
        ctx.ellipse(el.x + el.w / 2, el.y + el.h / 2, Math.abs(el.w / 2), Math.abs(el.h / 2), 0, 0, Math.PI * 2);
        ctx.stroke();
      } else if (el.tool === 'line' && el.points && el.points.length === 2) {
        ctx.beginPath();
        ctx.moveTo(el.points[0].x, el.points[0].y);
        ctx.lineTo(el.points[1].x, el.points[1].y);
        ctx.stroke();
      } else if (el.tool === 'text' && el.text && el.x != null && el.y != null) {
        ctx.font = `${el.lineWidth * 6 + 12}px Inter, system-ui`;
        ctx.fillText(el.text, el.x, el.y);
      }
    };

    elements.forEach(drawEl);
    if (currentEl) drawEl(currentEl);
  }, [elements, currentEl]);

  useEffect(() => { redraw(); }, [redraw]);

  const onDown = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const pos = getPos(e);
    setIsDrawing(true);
    startPos.current = pos;
    lastPos.current = pos;

    if (tool === 'text') {
      const text = window.prompt('Enter text:');
      if (text) {
        const el: DrawElement = { id: Date.now().toString(), tool: 'text', color, lineWidth, text, x: pos.x, y: pos.y };
        setUndoStack(u => [...u, elements]);
        setElements(els => [...els, el]);
      }
      return;
    }

    const el: DrawElement = {
      id: Date.now().toString(),
      tool,
      color: tool === 'eraser' ? '#000' : color,
      lineWidth,
      points: ['pen', 'eraser', 'line'].includes(tool) ? [pos] : undefined,
      x: pos.x, y: pos.y, w: 0, h: 0,
    };
    setCurrentEl(el);
  };

  const onMove = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isDrawing || !currentEl) return;
    const pos = getPos(e);

    setCurrentEl(el => {
      if (!el) return el;
      if (el.tool === 'pen' || el.tool === 'eraser') {
        return { ...el, points: [...(el.points || []), pos] };
      } else if (el.tool === 'line') {
        return { ...el, points: [el.points![0], pos] };
      } else {
        return { ...el, w: pos.x - (el.x || 0), h: pos.y - (el.y || 0) };
      }
    });
    lastPos.current = pos;
  };

  const onUp = () => {
    if (!isDrawing || !currentEl) return;
    setIsDrawing(false);
    setUndoStack(u => [...u, elements]);
    setElements(els => [...els, currentEl]);
    setCurrentEl(null);
    startPos.current = null;
    lastPos.current = null;
  };

  const undo = () => {
    if (undoStack.length === 0) return;
    const prev = undoStack[undoStack.length - 1];
    setElements(prev);
    setUndoStack(u => u.slice(0, -1));
  };

  const clearCanvas = () => {
    setUndoStack(u => [...u, elements]);
    setElements([]);
    addToast('info', 'Canvas cleared');
  };

  const saveCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = 'visual-export.png';
    a.click();
    addToast('success', 'Canvas exported as PNG!');
  };

  return (
    <div className="visual-tools-page">
      <PageHeader
        title="Visuals"
        breadcrumb={['Studio', 'Visuals']}
        actions={
          <div className="visual-header-actions">
            <Button variant="ghost" size="sm" onClick={undo}>↩ Undo</Button>
            <Button variant="ghost" size="sm" onClick={clearCanvas}>Clear</Button>
            <Button size="sm" onClick={saveCanvas}>Export PNG</Button>
          </div>
        }
      />

      <div className="visual-workspace">
        {/* Toolbar */}
        <div className="visual-toolbar">
          <div className="visual-toolbar__section">
            {TOOLS.map(t => (
              <button
                key={t.id}
                className={`vt-btn${tool === t.id ? ' vt-btn--active' : ''}`}
                title={t.label}
                onClick={() => setTool(t.id)}
              >
                {t.icon}
              </button>
            ))}
          </div>
          <div className="visual-toolbar__divider" />
          <div className="visual-toolbar__section">
            <div
              className="vt-color-swatch"
              style={{ background: color }}
              onClick={() => setPanel(p => p === 'colors' ? null : 'colors')}
              title="Color"
            />
            <div className="vt-size-wrap">
              <input
                type="range" min="1" max="12" value={lineWidth}
                onChange={e => setLineWidth(Number(e.target.value))}
                className="vt-size-slider"
                title="Brush size"
                style={{ accentColor: color }}
              />
            </div>
          </div>
          <div className="visual-toolbar__divider" />
          <div className="visual-toolbar__section">
            <div className="vt-zoom">
              <button className="vt-btn" onClick={() => setZoom(z => Math.max(50, z - 10))}>−</button>
              <span className="vt-zoom-val">{zoom}%</span>
              <button className="vt-btn" onClick={() => setZoom(z => Math.min(200, z + 10))}>+</button>
            </div>
          </div>

          {panel === 'colors' && (
            <div className="vt-color-panel">
              {COLORS.map(c => (
                <button
                  key={c}
                  className={`vt-color-btn${color === c ? ' vt-color-btn--active' : ''}`}
                  style={{ background: c }}
                  onClick={() => { setColor(c); setPanel(null); }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Canvas */}
        <div className="visual-canvas-wrap">
          <canvas
            ref={canvasRef}
            className="visual-canvas"
            width={1400}
            height={900}
            style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top left', cursor: tool === 'eraser' ? 'cell' : tool === 'text' ? 'text' : 'crosshair' }}
            onMouseDown={onDown}
            onMouseMove={onMove}
            onMouseUp={onUp}
            onMouseLeave={onUp}
            onTouchStart={onDown}
            onTouchMove={onMove}
            onTouchEnd={onUp}
          />
        </div>

        {/* Right panel: templates */}
        <div className="visual-right-panel">
          <div className="visual-panel-label">Templates</div>
          {TEMPLATES.map(t => (
            <button key={t.id} className="visual-template-btn" onClick={() => { clearCanvas(); addToast('info', `Loaded: ${t.label}`); }}>
              <span className="visual-template-icon">{t.icon}</span>
              <span>{t.label}</span>
            </button>
          ))}
          <div className="visual-panel-label" style={{ marginTop: '16px' }}>Stats</div>
          <div className="visual-stat">
            <span>Elements</span>
            <strong>{elements.length}</strong>
          </div>
          <div className="visual-stat">
            <span>Tool</span>
            <strong>{TOOLS.find(t => t.id === tool)?.label}</strong>
          </div>
          <div className="visual-stat">
            <span>Brush Size</span>
            <strong>{lineWidth}px</strong>
          </div>
          <div className="visual-stat">
            <span>Color</span>
            <strong>
              <span className="visual-stat-swatch" style={{ background: color }} />
              {color}
            </strong>
          </div>
        </div>
      </div>
    </div>
  );
}
